import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useHelpContent } from '../../utils/HelpUtils';

const isRTLText = (text: string): boolean => /[\u0590-\u05FF\u0600-\u06FF]/.test(text);

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return fallback;
}

export interface AdjustTextDialogProps {
  open: boolean;
  onClose: () => void;
  /** Initial plain text of the row being edited. */
  currentText: string;
  /** Previous neighbour text (omit/empty => no previous row). Borrowed text is anchored to its end. */
  prevText?: string;
  /** Next neighbour text (omit/empty => no next row). Borrowed text is anchored to its start. */
  nextText?: string;
  /**
   * Persists the edited text. Should throw on failure (the dialog shows the error and stays open)
   * and resolve on success (the parent is responsible for closing the dialog).
   */
  onSave: (newText: string) => void | Promise<void>;
}

/**
 * Lets the user borrow text from the end of the previous row and/or the start of the next row into
 * the current row, building a plain-text result. Selection in the neighbour fields is constrained
 * (clicking/dragging anchors to the row boundary) so only a suffix/prefix can be borrowed. Neither
 * neighbour row is modified — only the assembled text is returned via onSave.
 */
const AdjustTextDialog: React.FC<AdjustTextDialogProps> = ({
  open,
  onClose,
  currentText,
  prevText,
  nextText,
  onSave,
}) => {
  const { getTooltipTextForState } = useHelpContent();
  // Borrowed passages are tracked separately from the original text so the preview can highlight
  // exactly what was prepended/appended. `prepended` keeps the most recent borrow at its front.
  const [prepended, setPrepended] = useState('');
  const [appended, setAppended] = useState('');
  const [prevHasSelection, setPrevHasSelection] = useState(false);
  const [nextHasSelection, setNextHasSelection] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const prevRef = useRef<HTMLTextAreaElement | null>(null);
  const nextRef = useRef<HTMLTextAreaElement | null>(null);

  const hasPrev = !!prevText && prevText.length > 0;
  const hasNext = !!nextText && nextText.length > 0;

  const base = currentText.trim();
  // A "[...]" line marks each borrowed boundary: after the prepended passage and before the
  // appended one, signalling the omitted middle of the neighbouring row.
  const ELLIPSIS_SEPARATOR = '[...]';
  const composedText = [
    prepended.trim(),
    prepended.trim() ? ELLIPSIS_SEPARATOR : '',
    base,
    appended.trim() ? ELLIPSIS_SEPARATOR : '',
    appended.trim(),
  ]
    .filter((p) => p.length > 0)
    .join('\n');

  useEffect(() => {
    if (!open) return;
    setPrepended('');
    setAppended('');
    setPrevHasSelection(false);
    setNextHasSelection(false);
    setHint(null);
    setSaveError(null);
    setSaving(false);
  }, [open, currentText]);

  const refreshSelectionFlag = (which: 'prev' | 'next') => {
    const el = which === 'prev' ? prevRef.current : nextRef.current;
    const has = !!el && (el.selectionEnd ?? 0) > (el.selectionStart ?? 0);
    if (which === 'prev') {
      setPrevHasSelection(has);
    } else {
      setNextHasSelection(has);
    }
  };

  // Constrain marking so a selection in the previous row is always anchored to its end (a suffix)
  // and a selection in the next row is always anchored to its start (a prefix). Only re-anchor once
  // the user has an actual selection so focusing the field doesn't select everything.
  const clampSelection = (which: 'prev' | 'next') => {
    const el = which === 'prev' ? prevRef.current : nextRef.current;
    if (!el) return;
    const a = el.selectionStart ?? 0;
    const b = el.selectionEnd ?? 0;
    if (a === b) return;
    const value = el.value;
    if (which === 'prev') {
      const anchorEnd = value.trimEnd().length;
      const start = Math.min(Math.min(a, b), anchorEnd);
      if (a !== start || b !== anchorEnd) {
        el.setSelectionRange(start, anchorEnd);
      }
    } else {
      const anchorStart = value.length - value.trimStart().length;
      const end = Math.max(Math.max(a, b), anchorStart);
      if (a !== anchorStart || b !== end) {
        el.setSelectionRange(anchorStart, end);
      }
    }
  };

  // Click (or double-click) a spot to mark from that point to the boundary: previous → from the
  // clicked point to the end (a suffix), next → from the start to the clicked point (a prefix).
  const markFromPoint = (which: 'prev' | 'next') => {
    const el = which === 'prev' ? prevRef.current : nextRef.current;
    if (!el) return;
    const value = el.value;
    const a = el.selectionStart ?? 0;
    const b = el.selectionEnd ?? 0;
    if (which === 'prev') {
      const anchorEnd = value.trimEnd().length;
      const point = Math.min(Math.min(a, b), anchorEnd);
      el.setSelectionRange(point, anchorEnd);
    } else {
      const anchorStart = value.length - value.trimStart().length;
      const point = Math.max(Math.max(a, b), anchorStart);
      el.setSelectionRange(anchorStart, point);
    }
  };

  const insertFromNeighbour = (which: 'prev' | 'next') => {
    const el = which === 'prev' ? prevRef.current : nextRef.current;
    if (!el) return;
    const value = el.value;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selection = value.substring(start, end).trim();
    if (!selection) {
      setHint(
        which === 'prev'
          ? 'Select the text to borrow from the end of the previous row first.'
          : 'Select the text to borrow from the beginning of the next row first.',
      );
      return;
    }
    if (which === 'prev' && value.substring(end).trim() !== '') {
      setHint('In the previous row, your selection must reach the end of the row.');
      return;
    }
    if (which === 'next' && value.substring(0, start).trim() !== '') {
      setHint('In the next row, your selection must start at the beginning of the row.');
      return;
    }
    setHint(null);
    if (which === 'prev') {
      setPrepended((prev) => (prev ? `${selection} ${prev}` : selection));
    } else {
      setAppended((prev) => (prev ? `${prev} ${selection}` : selection));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(composedText);
    } catch (err: unknown) {
      setSaveError(extractApiErrorMessage(err, 'Failed to save the edited text.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Tooltip title={getTooltipTextForState('adjust-text-content', true)} placement="top">
          <span>Adjust text</span>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Previous row {hasPrev ? '(click to mark to end)' : '(none)'}
        </Typography>
        <TextField
          multiline
          fullWidth
          minRows={2}
          maxRows={6}
          value={prevText ?? ''}
          disabled={!hasPrev}
          placeholder={hasPrev ? '' : 'No previous row to borrow from.'}
          inputRef={prevRef}
          InputProps={{ readOnly: true }}
          inputProps={{
            dir: isRTLText(prevText ?? '') ? 'rtl' : 'ltr',
            onSelect: () => {
              clampSelection('prev');
              refreshSelectionFlag('prev');
            },
            onClick: () => {
              markFromPoint('prev');
              refreshSelectionFlag('prev');
            },
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasPrev || !prevHasSelection}
            onClick={() => insertFromNeighbour('prev')}
          >
            ↑ Prepend selected to this row
          </Button>
        </Box>

        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
          This row (preview){' '}
          {(prepended.trim() || appended.trim()) && (
            <Box
              component="span"
              sx={(theme) => ({
                ml: 0.5,
                px: 0.5,
                borderRadius: 0.5,
                fontWeight: 400,
                color: 'text.secondary',
                backgroundColor: alpha(theme.palette.warning.main, 0.35),
              })}
            >
              borrowed text highlighted
            </Box>
          )}
        </Typography>
        <Box
          dir={isRTLText(composedText) ? 'rtl' : 'ltr'}
          sx={(theme) => ({
            mb: 2,
            p: 1.5,
            minHeight: 88,
            maxHeight: 240,
            overflow: 'auto',
            borderRadius: 1,
            border: '1px solid',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            // Tint the edited row so it stands out from the (borrow-only) neighbour fields.
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.5),
          })}
        >
          {prepended.trim() && (
            <>
              <Box
                component="span"
                sx={(theme) => ({
                  borderRadius: 0.5,
                  px: 0.25,
                  backgroundColor: alpha(theme.palette.warning.main, 0.35),
                })}
              >
                {prepended.trim()}
              </Box>
              {`\n${ELLIPSIS_SEPARATOR}\n`}
            </>
          )}
          {base}
          {appended.trim() && (
            <>
              {`\n${ELLIPSIS_SEPARATOR}\n`}
              <Box
                component="span"
                sx={(theme) => ({
                  borderRadius: 0.5,
                  px: 0.25,
                  backgroundColor: alpha(theme.palette.warning.main, 0.35),
                })}
              >
                {appended.trim()}
              </Box>
            </>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Next row {hasNext ? '(click to mark from start)' : '(none)'}
        </Typography>
        <TextField
          multiline
          fullWidth
          minRows={2}
          maxRows={6}
          value={nextText ?? ''}
          disabled={!hasNext}
          placeholder={hasNext ? '' : 'No next row to borrow from.'}
          inputRef={nextRef}
          InputProps={{ readOnly: true }}
          inputProps={{
            dir: isRTLText(nextText ?? '') ? 'rtl' : 'ltr',
            onSelect: () => {
              clampSelection('next');
              refreshSelectionFlag('next');
            },
            onClick: () => {
              markFromPoint('next');
              refreshSelectionFlag('next');
            },
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasNext || !nextHasSelection}
            onClick={() => insertFromNeighbour('next')}
          >
            ↓ Append selected to this row
          </Button>
        </Box>

        {hint && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {hint}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          disabled={saving || (!prepended.trim() && !appended.trim())}
          onClick={() => {
            setPrepended('');
            setAppended('');
            setHint(null);
          }}
        >
          Reset
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={() => onClose()} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustTextDialog;
